import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { useProducts, Product } from '../../hooks/useProducts';
import { useCategories } from '../../hooks/useCategories';
import { useCart } from '../../contexts/CartContext';

function ProductCard({ product, onAddToCart, added }: {
  product: Product;
  onAddToCart: (e: React.MouseEvent, product: Product) => void;
  added: boolean;
}) {
  const allImages = [product.image_url, ...(product.galeri_gorseller || [])].filter(Boolean);
  const [imgIndex, setImgIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const handleMouseEnter = () => {
    setHovered(true);
    if (allImages.length <= 1) return;
    let idx = 1;
    intervalRef.current = setInterval(() => {
      setImgIndex(idx % allImages.length);
      idx++;
    }, 800);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setImgIndex(0);
  };

  return (
    <Link
      to={`/urun/${product.slug}`}
      className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 2:3 image area */}
      <div className="relative bg-cream overflow-hidden" style={{ aspectRatio: '2/3' }}>
        {allImages.length > 0 && (
          <img
            src={allImages[imgIndex]}
            alt={product.name}
            className="w-full h-full object-cover object-center transition-opacity duration-300"
          />
        )}
        {product.badge && (
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-gold text-white text-xs font-semibold rounded-lg">{product.badge}</span>
        )}
        {product.original_price && (
          <span className="absolute top-3 right-3 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-lg">
            %{Math.round((1 - product.price / product.original_price) * 100)}
          </span>
        )}
        {/* Nokta göstergesi */}
        {allImages.length > 1 && hovered && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {allImages.slice(0, 5).map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${i === imgIndex ? 'bg-white scale-125' : 'bg-white/50'}`} />
            ))}
          </div>
        )}
        <button
          onClick={(e) => onAddToCart(e, product)}
          className={`absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${added ? 'bg-green-500' : 'bg-coffee hover:bg-gold'} text-white opacity-0 group-hover:opacity-100`}
          aria-label="Sepete ekle"
        >
          <i className={`text-base ${added ? 'ri-check-line' : 'ri-shopping-bag-line'}`} />
        </button>
      </div>
      <div className="p-4">
        <p className="text-xs text-gold font-medium mb-1">{product.category_name}</p>
        <h3 className="font-serif text-sm font-semibold text-coffee mb-2 group-hover:text-gold transition-colors line-clamp-2">{product.name}</h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold text-coffee">{product.price.toLocaleString('tr-TR')}₺</span>
            {product.original_price && (
              <span className="text-xs text-coffee-light line-through">{product.original_price.toLocaleString('tr-TR')}₺</span>
            )}
          </div>
          {product.weight && <span className="text-xs text-coffee-light">{product.weight}</span>}
        </div>
      </div>
    </Link>
  );
}

const sortOptions = [
  { value: 'default', label: 'Öne Çıkanlar' },
  { value: 'price_asc', label: 'Fiyat: Artan' },
  { value: 'price_desc', label: 'Fiyat: Azalan' },
];

export default function ProductsPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [sort, setSort] = useState('default');
  const [addedId, setAddedId] = useState<string | null>(null);
  const { addItem } = useCart();
  const [params] = useSearchParams();

  const categoryName = params.get('kategori');
  const { products, loading } = useProducts();
  const { categories } = useCategories();

  const activeCategory = useMemo(
    () => categories.find(c => c.name === categoryName) ?? null,
    [categories, categoryName]
  );

  const filteredProducts = useMemo(() => {
    let list = categoryName
      ? products.filter(p => p.category_name === categoryName)
      : products;

    if (sort === 'price_asc') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'price_desc') list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, categoryName, sort]);

  const handleAddToCart = (e: React.MouseEvent, product: ReturnType<typeof useProducts>['products'][0]) => {
    e.preventDefault();
    if (!product.stock || product.stock <= 0) return;
    addItem({
      id: product.id,
      product_id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      slug: product.slug,
      weight: product.weight,
      stock: product.stock,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1800);
  };

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-2 text-xs text-coffee-light mb-8">
            <Link to="/" className="hover:text-gold transition-colors">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-coffee">{categoryName || 'Tüm Ürünler'}</span>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-60 flex-shrink-0">
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-serif text-base font-semibold text-coffee mb-4">Kategoriler</h3>
                <ul className="space-y-1">
                  <li>
                    <Link
                      to="/urunler"
                      className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${!categoryName ? 'bg-coffee text-cream font-medium' : 'text-coffee-medium hover:bg-cream'}`}
                    >
                      <span>Tüm Ürünler</span>
                      <span className="text-xs">{products.length}</span>
                    </Link>
                  </li>
                  {categories.map(cat => (
                    <li key={cat.id}>
                      <Link
                        to={`/urunler?kategori=${encodeURIComponent(cat.name)}`}
                        className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${categoryName === cat.name ? 'bg-coffee text-cream font-medium' : 'text-coffee-medium hover:bg-cream'}`}
                      >
                        <span>{cat.name}</span>
                        <span className="text-xs">{cat.product_count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Products */}
            <div className="flex-1">
              {activeCategory?.description && (
                <div className="flex items-start gap-3 bg-white rounded-2xl px-5 py-4 mb-6 border-l-4 border-gold">
                  <i className="ri-information-line text-gold text-lg mt-0.5 flex-shrink-0" />
                  <div>
                    <h2 className="font-serif text-sm font-semibold text-coffee mb-0.5">{activeCategory.name}</h2>
                    <p className="text-sm text-coffee-medium leading-relaxed">{activeCategory.description}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-coffee-light">
                  <strong className="text-coffee">{filteredProducts.length}</strong> ürün bulundu
                </p>
                <select
                  value={sort}
                  onChange={e => setSort(e.target.value)}
                  className="text-sm text-coffee bg-white border border-cream-dark rounded-xl px-3 py-2 focus:outline-none focus:border-gold cursor-pointer"
                >
                  {sortOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {[1,2,3,4,5,6,7,8].map(i => (
                    <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                      <div className="bg-cream" style={{ aspectRatio: '2/3' }} />
                      <div className="p-4 space-y-2">
                        <div className="h-3 bg-cream rounded w-2/3" />
                        <div className="h-4 bg-cream rounded" />
                        <div className="h-5 bg-cream rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-coffee-light mb-4 block" />
                  <p className="text-coffee-medium font-medium mb-2">Henüz ürün yok</p>
                  <p className="text-sm text-coffee-light">Admin panelinden ürün ekleyebilirsiniz.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onAddToCart={handleAddToCart}
                      added={addedId === product.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
